import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import styled from 'styled-components';
import Table from 'core/components/HTMLTable';
import { Button, Card, Collapse } from 'core/components';
import CodeMirror from 'core/components/CodeMirror';

const AnchorComponent = ({ node, href, children, ...props }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
    {children}
  </a>
);

const TableComponent = ({ node, ...props }) => (
  <Card elevation={1} mb={2}>
    <Table {...props} style={{ width: '100%' }} interactive bordered striped>
      {props.children}
    </Table>
  </Card>
);

const CodeBlockComponent = ({ children }) => {
  const textContent = (children?.props?.children || '').trim();

  return (
    <CodeMirror
      value={textContent}
      options={{
        readOnly: true
      }}
    />
  );
};

const BlockQuoteComponent = ({ children }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Get the text content from children
  const getTextContent = (node) => {
    if (typeof node === 'string') {
      return node;
    }
    if (React.isValidElement(node) && node.props.children) {
      return getTextContent(node.props.children);
    }
    if (Array.isArray(node)) {
      return node.map(getTextContent).join('');
    }
    return '';
  };

  const textContent = getTextContent(children);
  const shouldCollapse = textContent.length > 200;
  const truncatedContent = shouldCollapse ? `${textContent.substring(0, 200)}...` : textContent;

  const toggleExpanded = () => setIsExpanded(!isExpanded);

  return (
    <blockquote>
      {shouldCollapse ? (
        <>
          <Collapse isOpen={isExpanded}>{children}</Collapse>
          {!isExpanded && (
            <>
              {truncatedContent}
              <Button small onClick={toggleExpanded} style={{ display: 'block' }}>
                Show more...
              </Button>
            </>
          )}
          {isExpanded && (
            <Button small onClick={toggleExpanded} style={{ display: 'block' }}>
              Show less
            </Button>
          )}
        </>
      ) : (
        children
      )}
    </blockquote>
  );
};

// align center is the default, but leaving this here in case Dave changes his mind.
const MarkdownWrap = styled.div`
  .katex-display > .katex {
    text-align: center;
  }

  .CodeMirror-wrap {
    border-radius: 8px;
    margin-bottom: 16px;
  }

  line-height: 1.7;

  strong {
    font-weight: 700;
  }

  code {
    font-family: ${({ theme }) => theme.fonts.monospace};
    background: #4e4a67;
    font-size: 12px;
    font-weight: 500;
    padding: 3px 6px;
    border-radius: 4px;
    color: #ffffff;
  }

  p {
    margin-bottom: 8px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  ol,
  ul {
    margin-bottom: 16px;
    padding-left: 24px;

    &:last-child {
      margin-bottom: 0;
    }

    li {
      margin-bottom: 8px;

      ul {
        margin-top: 6px;
        margin-bottom: 6px;
      }

      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  blockquote {
    font-size: 11px;
    margin: 8px 0px;
    padding: 2px 12px;
    border-left: 2px solid ${({ theme }) => theme.borderColors.thin};
  }

  hr {
    border: none;
    border-top: 1px solid rgba(0, 0, 0, 0.1);
    margin: 2rem 0;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin-top: 16px;
    margin-bottom: 8px;
    font-weight: 700;
  }

  h1 {
    font-size: 20px;
  }

  h2 {
    font-size: 18px;
  }

  h3 {
    font-size: 16px;
  }

  h4,
  h5 {
    font-size: 14px;
  }

  h6 {
    font-size: 12px;
  }

  font-size: 14px;
`;

class Markdown extends React.Component {
  render() {
    const { children } = this.props;
    return (
      <MarkdownWrap>
        <ReactMarkdown
          components={{
            a: AnchorComponent,
            table: TableComponent,
            pre: CodeBlockComponent,
            blockquote: BlockQuoteComponent
          }}
          remarkPlugins={[remarkGfm, remarkMath]}
          rehypePlugins={[rehypeKatex]}
        >
          {children}
        </ReactMarkdown>
      </MarkdownWrap>
    );
  }
}

export default Markdown;
